import Styled, { css } from 'styled-components';
import curry from 'lodash/curry';
import merge from 'lodash/merge';

import {
  cssDeclaration,
  VALID_DIMENSION_DATA,
  VALID_EDGE_STYLE_DATA,
  VALID_MEDIA_STYLES,
} from '../Utils';
import IThemeInterface from '../Theme/common';
import { VALID_GLOBAL_FONT_SIZES } from '../Typography/common';

import { IButtonCommonProps, IButtonTheme } from './common';

interface IBTNBase extends IButtonCommonProps {
  marginProp?: VALID_EDGE_STYLE_DATA;
  orderProp?: VALID_MEDIA_STYLES;
}

const BtnBase = css<IBTNBase>`
  display: block;
  position: relative;
  border: 0;
  transition: color 100ms ease-out, background 100ms ease-out;
  font-style: normal;
  font-size: 1.6rem;
  font-weight: normal;
  line-height: 1.2;
  text-align: center;

  ${({
    theme: { themeTwo },
    alignSelf,
    flexBasis,
    flexGrow,
    flexShrink,
    marginProp,
    orderProp,
  }) => css`
    /* margin */ ${marginProp && cssDeclaration(themeTwo, 'margin', marginProp)};
    /* align-self */ ${alignSelf && cssDeclaration(themeTwo, 'align-self', alignSelf)};
    /* flex-basis */ ${flexBasis && cssDeclaration(themeTwo, 'flex-basis', flexBasis)};
    /* flex-grow */ ${flexGrow && cssDeclaration(themeTwo, 'flex-grow', flexGrow)};
    /* flex-shrink */ ${flexShrink && cssDeclaration(themeTwo, 'flex-shrink', flexShrink)};
    /* order */ ${orderProp && cssDeclaration(themeTwo, 'order', orderProp)};
  `}

  span,
  svg {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const makeButtonCSS = (theme: IThemeInterface, btnTheme: IButtonTheme) =>
  css`
    /* border-radius */ ${cssDeclaration(theme, 'border-radius', btnTheme.borderRadius)};
    /* padding */ ${cssDeclaration(theme, 'padding', btnTheme.padding)};
    /* border */ ${cssDeclaration(theme, 'border', btnTheme.border)};
    /* box-shadow */ ${cssDeclaration(theme, 'box-shadow', btnTheme.shadow)};
    /* width */ ${cssDeclaration(theme, 'width', btnTheme.width)};
    /* outline */ ${cssDeclaration(theme, 'outline', btnTheme.outline)};
    /* font-size */ ${btnTheme.font.size && cssDeclaration(theme, 'font-size', btnTheme.font.size as VALID_GLOBAL_FONT_SIZES)};
    /* background-color */ ${cssDeclaration(theme, 'background-color', btnTheme.background)};
    /* color */ ${btnTheme.font.color && cssDeclaration(theme, 'color', btnTheme.font.color)};

    line-height: ${btnTheme.font.lineHeight};
    letter-spacing: ${btnTheme.font.letterSpacing && theme.global.edgeSize[btnTheme.font.letterSpacing] || btnTheme.font.letterSpacing};
    font-weight: ${btnTheme.font.weight};
    text-transform: ${btnTheme.font.textTransform};
    text-decoration: ${btnTheme.font.textDecoration};
    width: 100%;

    svg { fill: currentColor; }

    span {
      ${btnTheme.justifyContent && css`justify-content: ${btnTheme.justifyContent}`};
    }

    /* keep as last item */
    ${btnTheme.additionalStyles};
  `;

export const makeVariantTheme = (theme: IThemeInterface, variant: string): { [index: string]: IButtonTheme } => {

  const buttonDefault = theme.button.base;
  const variantBase = theme.button.overrides[variant].base;
  const variantState = theme.button.overrides[variant].state;
  const variantTheme = merge({}, buttonDefault, variantBase);

  return {
    variantTheme,
    variantActiveTheme: merge({}, variantTheme, variantState.active),
    variantHoverTheme: merge({}, variantTheme, variantState.hover),
    variantFocusTheme: merge({}, variantTheme, variantState.focus),
    variantDisabledTheme: merge({}, variantTheme, variantState.disabled),
    variantErrorTheme: merge({}, variantTheme, variantState.error),
    variantSelectedTheme: merge({}, variantTheme, variantState.selected),
    variantIsLoadingTheme: merge({}, variantTheme, variantState.isLoading),
  };
};

export const makeThemedButtonCSS = (
  theme: IThemeInterface,
  variant: string,
  isLoading?: boolean,
  widthProp?: VALID_DIMENSION_DATA,
  error?: boolean,
) => {
  const buildCSS = curry(makeButtonCSS)(theme);

  const themeObj = makeVariantTheme(theme, variant);

  const themeBase = buildCSS(themeObj.variantTheme);
  const active = buildCSS(themeObj.variantActiveTheme);
  const hover = buildCSS(themeObj.variantHoverTheme);
  const focus = buildCSS(themeObj.variantFocusTheme);
  const disabled = buildCSS(themeObj.variantDisabledTheme);
  const errorTheme = buildCSS(themeObj.variantErrorTheme);
  const isLoadingTheme = buildCSS(themeObj.variantIsLoadingTheme);
  const widthOverride = widthProp ? cssDeclaration(theme, 'width', widthProp) : undefined;

  return css`

    ${ BtnBase }

    ${ themeBase }

    ${ widthOverride }

    &:hover {
      ${ isLoading ? themeBase : hover }
      ${ widthOverride }
      ${error && errorTheme}
    }

    &:focus:not(:focus-visible) {
      outline: 0;
      box-shadow: none;
    }

    &:focus-visible,
    &:focus-within {
      ${focus}
      ${widthOverride}
    }

    &:active {
      ${ active }
      ${ widthOverride }
    }

    &:disabled {
      // disabled override is required - potentially look to an even safer fallback for disabled state
      cursor: not-allowed;
      ${ disabled }
      ${ widthOverride }
      ${isLoading && isLoadingTheme}
    }

    ${error && errorTheme}
  `;
};

export interface IThemedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, IBTNBase {
  variant: string;
  isLoading?: boolean;
  error?: boolean;
  widthProp?: VALID_DIMENSION_DATA;
}

export const ThemedButton = Styled.button<IThemedButtonProps>`
  ${({ theme: { themeTwo }, variant, isLoading, widthProp, error }) => makeThemedButtonCSS(themeTwo, variant, isLoading, widthProp, error)}
`;
